/**
 * Filter Persistence Utility
 * Provides centralized localStorage management for filters across all pages
 * Filters expire after 24 hours by default
 */

import { useState, useCallback } from 'react';

const DEFAULT_EXPIRY_HOURS = 24;

/**
 * Save filter state to localStorage with expiration
 * @param {string} key - Unique key for the filter (e.g., 'locationFilters', 'deviceFilters')
 * @param {any} value - Filter value to store
 * @param {number} expiryHours - Hours until expiration (default: 24)
 */
export const saveFilter = (key, value, expiryHours = DEFAULT_EXPIRY_HOURS) => {
  try {
    if (value === null || value === undefined || value === '' || (Array.isArray(value) && value.length === 0)) {
      // Remove empty values
      localStorage.removeItem(key);
      localStorage.removeItem(`${key}_expiry`);
      return;
    }

    const serialized = typeof value === 'string' ? value : JSON.stringify(value);
    localStorage.setItem(key, serialized);

    const expiryTime = Date.now() + (expiryHours * 60 * 60 * 1000);
    localStorage.setItem(`${key}_expiry`, String(expiryTime));
  } catch (error) {
    console.error(`Failed to save filter "${key}":`, error);
  }
};

/**
 * Load filter state from localStorage
 * @param {string} key - Unique key for the filter
 * @param {any} defaultValue - Default value if not found or expired
 * @returns {any} Stored value or default value
 */
export const loadFilter = (key, defaultValue = null) => {
  try {
    // Check expiration
    const expiryTime = localStorage.getItem(`${key}_expiry`);
    if (expiryTime && Date.now() > parseInt(expiryTime)) {
      // Expired - clear and return default
      clearFilter(key);
      return defaultValue;
    }

    const stored = localStorage.getItem(key);
    if (!stored) {
      return defaultValue;
    }

    // Try to parse as JSON, fallback to string
    try {
      return JSON.parse(stored);
    } catch {
      return stored;
    }
  } catch (error) {
    console.error(`Failed to load filter "${key}":`, error);
    return defaultValue;
  }
};

/**
 * Clear specific filter from localStorage
 * @param {string} key - Unique key for the filter
 */
export const clearFilter = (key) => {
  try {
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_expiry`);
  } catch (error) {
    console.error(`Failed to clear filter "${key}":`, error);
  }
};

/**
 * Clear all filters with a specific prefix
 * @param {string} prefix - Prefix to match (e.g., 'location' to clear all location-related filters)
 */
export const clearFiltersByPrefix = (prefix) => {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.error(`Failed to clear filters with prefix "${prefix}":`, error);
  }
};

/**
 * Clear all expired filters from localStorage
 * Should be called on app initialization
 */
export const clearExpiredFilters = () => {
  try {
    const keys = Object.keys(localStorage);
    const now = Date.now();

    keys.forEach(key => {
      if (key.endsWith('_expiry')) {
        const expiryTime = parseInt(localStorage.getItem(key));
        if (now > expiryTime) {
          const filterKey = key.replace('_expiry', '');
          clearFilter(filterKey);
        }
      }
    });
  } catch (error) {
    console.error('Failed to clear expired filters:', error);
  }
};

/**
 * React hook for filter persistence
 * @param {string} key - Unique key for the filter
 * @param {any} initialValue - Initial value if not found
 * @param {number} expiryHours - Hours until expiration
 * @returns {[any, Function]} [value, setValue] tuple
 */
export const usePersistedFilter = (key, initialValue = null, expiryHours = DEFAULT_EXPIRY_HOURS) => {
  const [value, setValue] = useState(() => loadFilter(key, initialValue));

  const setPersistedValue = useCallback((newValue) => {
    setValue(newValue);
    saveFilter(key, newValue, expiryHours);
  }, [key, expiryHours]);

  return [value, setPersistedValue];
};

// Filter key constants for consistency across the app
export const FILTER_KEYS = {
  LOCATION: {
    SEARCH: 'locationSearchTerm',
    ACTIVE_FILTERS: 'locationActiveFilters',
    EXPANDED_NODES: 'locationExpandedNodes',
  },
  DEVICE: {
    SEARCH: 'deviceSearchTerm',
    ACTIVE_FILTERS: 'deviceActiveFilters',
    VIEW_MODE: 'deviceViewMode',
    STATUS_FILTER: 'deviceStatusFilter',
    TYPE_FILTER: 'deviceTypeFilter',
    LOCATION_FILTER: 'deviceLocationFilter',
  },
  TICKET: {
    SEARCH: 'ticketSearchTerm',
    ACTIVE_FILTERS: 'ticketActiveFilters',
    STATUS_FILTER: 'ticketStatusFilter',
    URGENCY_FILTER: 'ticketUrgencyFilter',
    SUPPORT_TYPE_FILTER: 'ticketSupportTypeFilter',
    NEXT_STEP_FILTER: 'ticketNextStepFilter',
    VIEW_MODE: 'ticketViewMode',
    PAGE: 'ticketPage',
    ROWS_PER_PAGE: 'ticketRowsPerPage',
  },
  FIELD_INTERVENTION: {
    SEARCH: 'fieldInterventionSearchTerm',
    ACTIVE_FILTERS: 'fieldInterventionActiveFilters',
    STATUS_FILTER: 'fieldInterventionStatusFilter',
    OUTCOME_FILTER: 'fieldInterventionOutcomeFilter',
    LOCATION_FILTER: 'fieldInterventionLocationFilter',
    SHOW_UNINVOICED_ONLY: 'fieldInterventionShowUninvoicedOnly',
    PAGE: 'fieldInterventionPage',
    LIMIT: 'fieldInterventionLimit',
  },
  ALARM: {
    SEARCH: 'alarmSearchTerm',
    ACTIVE_FILTERS: 'alarmActiveFilters',
    STATUS_FILTER: 'alarmStatusFilter',
    SEVERITY_FILTER: 'alarmSeverityFilter',
    QUICK_FILTER: 'alarmQuickFilter',
    DATE_FROM: 'alarmDateFrom',
    DATE_TO: 'alarmDateTo',
    AUDIO_ENABLED: 'alarmAudioEnabled',
  },
  USER: {
    SEARCH: 'userSearchTerm',
  },
  COMPANY: {
    SEARCH: 'companySearchTerm',
  },
  CAMERA_REQUEST: {
    CURRENT_TAB: 'cameraRequestCurrentTab',
    LOCATION_FILTER: 'cameraRequestLocationFilter',
  }
};

export default {
  saveFilter,
  loadFilter,
  clearFilter,
  clearFiltersByPrefix,
  clearExpiredFilters,
  usePersistedFilter,
  FILTER_KEYS,
};
